import { Container } from 'pixi.js';
import { getLocalizedText } from '@softgames/localization';
import type { GameTutorialMoveStep, GameTutorialGoalStep } from './types';
import { GameTutorialStepType } from './types';
import { TutorialMovementArrowView } from './components/TutorialMovementArrowView';
import { setContainerParent } from '../../../utils/pixi/transform-utils';
import { TutorialTextBannerView } from './components/TutorialTextBannerView';
import type { GameAreaView } from '../../boardMVC/GameAreaView';
import { TutorialHintArrowView } from './components/TutorialHintArrowView';
import type { TopBarView } from '../../views/TopBarView';
import { registerResizableObject } from '../../../utils/pixi/registerResizableObject';
import { waitForNextFrame } from '../../../utils/async-utils';

export interface GameTutorialViewConfig {
  readonly overlay: Container;
  readonly topBarView: TopBarView;
  readonly gameAreaView: GameAreaView;
}

export class GameTutorialView extends Container {
  private readonly movementArrow = new TutorialMovementArrowView().props({
    parent: this,
    visible: false,
  });

  public readonly hintArrow = new TutorialHintArrowView().props({
    parent: this,
  });

  /** Whether the movement arrow should be visible for the current step. */
  private movementArrowVisible = false;

  /** Number of in-flight resize() calls; the cursor is only restored when it reaches 0. */
  private resizeDepth = 0;

  constructor(private readonly config: GameTutorialViewConfig) {
    super();

    config.overlay.addChild(this);

    registerResizableObject(this);
  }

  public destroy(): void {
    this.movementArrow.destroy();
    this.hintArrow.destroy();
    super.destroy({ children: true });
  }

  public showMovementStep(step: GameTutorialMoveStep): void {
    const {
      gameAreaView: { boardView, trayView },
    } = this.config;

    const { movementArrow } = this;

    this.clear();

    if (step.type === GameTutorialStepType.Move) {
      const item = trayView.getItemByIndex(step.trayX);
      const stackView = boardView.getStackViewByGridXY(step.stackX, step.stackY);

      if (stackView && item) {
        movementArrow.animatePos = 0;
        movementArrow.pointAt(item.background as Container, stackView);
        movementArrow.update(0);
        movementArrow.visible = true;
        this.movementArrowVisible = true;
      }
    }
    void this.resize();

    boardView.highlightLayer.lockSelection('tutorialView');
    boardView.highlightLayer.setXYHighlight(step.stackX, step.stackY, true);
  }

  public isMovementStepComplete(step: GameTutorialMoveStep): boolean {
    const {
      gameAreaView: { boardView },
    } = this.config;
    return !!boardView.getStackViewByGridXY(step.stackX, step.stackY);
  }

  public endMovementStep(step: GameTutorialMoveStep): void {
    const {
      gameAreaView: { boardView },
    } = this.config;

    boardView.highlightLayer.setXYHighlight(step.stackX, step.stackY, false);
    boardView.highlightLayer.unlockSelection('tutorialView');
  }

  public showGoalStep(step: GameTutorialGoalStep, onComplete: () => void): void {
    const { hintArrow } = this;
    const { overlay } = this.config;
    const { topBarView } = this.config;
    const tasksContainer = topBarView.getTasksContainer();
    const { resetHighlight, target: highlightTarget } = topBarView.highlightGoal(overlay);

    // Show tutorial text overlay.
    const textOverlay = TutorialTextBannerView.show({
      text: getLocalizedText(step.text),
      overlay,
      onClickAction: () => {
        resetHighlight();
        setContainerParent(hintArrow, this);
        void hintArrow.hide();
      },
      onOutComplete: onComplete,
    });

    // Fix depth of goal highlight
    highlightTarget.parent?.addChild?.(highlightTarget);

    const taskContainerPos = textOverlay.toLocal(
      tasksContainer.position,
      tasksContainer?.parent ?? tasksContainer.pixi.stage,
    );
    this.hintArrow.reset().props({
      parent: textOverlay,
      x: taskContainerPos.x + 20,
      y: taskContainerPos.y + 80,
      scaleX: 1.5,
      scaleY: -1.5,
    });
    hintArrow.show();
  }

  public update(deltaTime: number): void {
    const { movementArrow, hintArrow } = this;
    if (movementArrow.visible) movementArrow.update(deltaTime);
    if (hintArrow.alpha > 0) hintArrow.update(deltaTime);
  }

  public async resize(): Promise<void> {
    const {
      gameAreaView: { boardView },
    } = this.config;

    // The board transform only settles on a later frame, so hide the cursor while we
    // wait; otherwise update() keeps drawing it at a stale position/scale for a couple
    // of frames after an orientation change, which reads as flickering.
    //
    // The depth counter makes overlapping resize() calls safe (several 'resize' events
    // fire during a rotation): we only restore visibility once the last one finishes,
    // and the finally guarantees the cursor is never left permanently hidden.
    this.resizeDepth += 1;
    this.movementArrow.visible = false;

    try {
      await waitForNextFrame();

      if (boardView.destroyed || this.destroyed) return;

      this.position.set(boardView.x, boardView.y);
      this.scale.set(boardView.scale.x, boardView.scale.y);
      this.movementArrow.resize();
    } finally {
      this.resizeDepth -= 1;
      if (this.resizeDepth === 0 && !this.destroyed) {
        this.movementArrow.visible = this.movementArrowVisible;
      }
    }
  }

  public clear(): void {
    this.movementArrowVisible = false;
    this.movementArrow?.props({
      visible: false,
    });
  }
}
